import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { ErrorMessage } from 'app/core/error-handler/constants';
import { Error } from 'app/core/error-handler/models';
import { createErrorRaised } from 'app/core/error-handler/state/actions';
import { Task, TaskInterface, TaskTemplateInterface } from 'app/task/models';
import { TaskRestConnectorService, TaskServiceFilters } from 'app/task/services';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import {
  CloseTask,
  CloseTaskCompleted,
  CreateSurveyTask,
  CreateTask,
  CreateTaskCompleted,
  LoadTasks,
  LoadTasksCompleted,
  LoadTaskTemplatesCompleted,
  TaskActionType,
  UpdateTask,
  UpdateTaskCompleted,
} from './actions';

@Injectable()
export class TaskEffects {
  public source: string = 'TaskEffects';

  /**
   * Load task templates.
   */
  @Effect()
  public loadTaskTemplates$: Observable<Action> = this._actions$
    .pipe(
      ofType(TaskActionType.LoadTaskTemplates),
      switchMap(() => {
        return this._taskConnector.loadTaskTemplates()
        .pipe(
          map((taskTemplates: TaskTemplateInterface[]) =>
            new LoadTaskTemplatesCompleted(taskTemplates)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }),
    );


  /**
   * Load all available tasks.
   */
  @Effect()
  public loadTasks$: Observable<Action> = this._actions$
    .pipe(
      ofType(TaskActionType.LoadTasks),
      map((action: LoadTasks) => action.payload),
      switchMap((loadTaskFilters: TaskServiceFilters) => {
        return this._taskConnector.loadAll(loadTaskFilters)
        .pipe(
          map((tasks: Task[]) => new LoadTasksCompleted(tasks)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }),
    );

  /**
   * Update the provided task.
   */
  @Effect()
  public updateTask$: Observable<Action> = this._actions$
    .pipe(
      ofType(TaskActionType.UpdateTask),
      map((action: UpdateTask) => action.payload),
      switchMap((task: TaskInterface) => {
        return this._taskConnector.update(task)
        .pipe(
          map((task: Task) => new UpdateTaskCompleted(task)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }),
    );

  /**
   * Create the provided task.
   */
  @Effect()
  public createTask$: Observable<Action> = this._actions$
    .pipe(
      ofType(TaskActionType.CreateTask),
      map((action: CreateTask) => action.payload),
      switchMap((task: TaskInterface) => {
        return this._taskConnector.create(task)
        .pipe(
          map((task: Task) => new CreateTaskCompleted(task)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }),
    );

  @Effect()
  public createSurveyTask$: Observable<Action> = this._actions$
    .pipe(
      ofType(TaskActionType.CreateSurveyTask),
      map((action: CreateSurveyTask) => action.payload),
      switchMap((task: TaskInterface) => {
        return this._taskConnector.createSurveyTask(task)
        .pipe(
          map((task: Task) => new CreateTaskCompleted(task)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }),
    );

  /**
   * Close the provided task.
   */
  @Effect()
  public closeTask$: Observable<Action> = this._actions$
    .pipe(
      ofType(TaskActionType.CloseTask),
      map((action: CloseTask) => action.payload),
      switchMap((task: TaskInterface) => {
        return this._taskConnector.close(task)
        .pipe(
          map((task: Task) => new CloseTaskCompleted(task)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }),
    );


  constructor(private readonly _actions$: Actions, private readonly _taskConnector: TaskRestConnectorService) {}

}
